function initCarousel(carousel) {
  const stage = carousel.querySelector('.carousel__stage');
  const track = carousel.querySelector('.carousel__track');
  const indicators = Array.from(carousel.querySelectorAll('.carousel__indicator'));

  if (!stage || !track) return;

  const originalCards = Array.from(track.querySelectorAll('.carousel__card'));
  if (originalCards.length < 2) return;

  const AUTOPLAY_DELAY = 4000;
  const TRANSITION_MS = 500;
  const MANUAL_INTERACTION_DELAY = 8000;
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  let autoplayTimer = null;
  let animationTimeout = null;
  let manualInteractionTimer = null;
  let isAnimating = false;
  let touchStartX = 0;
  let touchEndX = 0;
  let isSwiping = false;

  const prependClones = [
    originalCards[originalCards.length - 2].cloneNode(true),
    originalCards[originalCards.length - 1].cloneNode(true),
  ];

  const appendClones = [originalCards[0].cloneNode(true), originalCards[1].cloneNode(true)];

  [...prependClones, ...appendClones].forEach((clone) => {
    clone.setAttribute('data-clone', 'true');
    clone.setAttribute('aria-hidden', 'true');
  });

  track.insertBefore(prependClones[0], originalCards[0]);
  track.insertBefore(prependClones[1], originalCards[0]);
  track.appendChild(appendClones[0]);
  track.appendChild(appendClones[1]);

  const allCards = Array.from(track.querySelectorAll('.carousel__card'));
  let currentIndex = 3;

  function getGap() {
    const styles = window.getComputedStyle(track);
    return parseFloat(styles.columnGap || styles.gap || '0') || 0;
  }

  function getStep() {
    const card = allCards[currentIndex];
    return card.getBoundingClientRect().width + getGap();
  }

  function getRealIndex(trackIndex) {
    if (trackIndex < 2) {
      return originalCards.length - 1 - trackIndex;
    }

    if (trackIndex >= originalCards.length + 2) {
      return trackIndex - (originalCards.length + 2);
    }

    return trackIndex - 2;
  }

  function updateIndicators(realIndex) {
    indicators.forEach((indicator, index) => {
      const icon = indicator.querySelector('.carousel__indicator-icon');
      const iconSrc =
        index === realIndex
          ? '/images/svgs/carousel-indicator--selected.svg'
          : '/images/svgs/carousel-indicator--default.svg';

      if (index === realIndex) {
        indicator.setAttribute('aria-current', 'true');
      } else {
        indicator.removeAttribute('aria-current');
      }

      if (icon) {
        icon.setAttribute('src', iconSrc);
      }
    });
  }

  function updateCardStates() {
    const realIndex = getRealIndex(currentIndex);

    allCards.forEach((card) => {
      card.classList.remove('is-prev', 'is-active', 'is-next');
      card.setAttribute('aria-hidden', 'true');
    });

    const prevIndex = currentIndex - 1;
    const nextIndex = currentIndex + 1;

    if (allCards[prevIndex]) {
      allCards[prevIndex].classList.add('is-prev');
    }

    if (allCards[currentIndex]) {
      allCards[currentIndex].classList.add('is-active');
      if (!allCards[currentIndex].hasAttribute('data-clone')) {
        allCards[currentIndex].removeAttribute('aria-hidden');
      }
    }

    if (allCards[nextIndex]) {
      allCards[nextIndex].classList.add('is-next');
    }

    updateIndicators(realIndex);
  }

  function disableCardScaleTransitions() {
    allCards.forEach((card) => {
      const inner = card.querySelector('.carousel__card-inner');
      if (inner) {
        inner.style.transition = 'none';
      }
    });
  }

  function enableCardScaleTransitions() {
    allCards.forEach((card) => {
      const inner = card.querySelector('.carousel__card-inner');
      if (inner) {
        inner.style.transition = '';
      }
    });
  }

  function setTrackPosition(index, animate = true) {
    const step = getStep();
    const cardWidth = allCards[index].getBoundingClientRect().width;
    const stageWidth = stage.getBoundingClientRect().width;
    const centerOffset = (stageWidth - cardWidth) / 2;
    const translate = -(index * step) + centerOffset;

    track.style.transition = animate ? `transform ${TRANSITION_MS}ms cubic-bezier(0.22, 0.61, 0.36, 1)` : 'none';

    track.style.transform = `translate3d(${translate}px, 0, 0)`;
  }

  function stopAutoplay() {
    if (autoplayTimer !== null) {
      clearTimeout(autoplayTimer);
      autoplayTimer = null;
    }
  }

  function startAutoplay() {
    stopAutoplay();

    if (manualInteractionTimer !== null) return;
    if (prefersReducedMotion) return;
    if (document.hidden) return;
    if (isAnimating) return;

    autoplayTimer = window.setTimeout(() => {
      goToAutoIndex(currentIndex + 1);
    }, AUTOPLAY_DELAY);
  }

  function resetManualInteractionTimer() {
    if (manualInteractionTimer !== null) {
      clearTimeout(manualInteractionTimer);
    }

    manualInteractionTimer = window.setTimeout(() => {
      manualInteractionTimer = null;
      startAutoplay();
    }, MANUAL_INTERACTION_DELAY);
  }

  function snapIfNeeded() {
    let didSnap = false;

    if (currentIndex < 2 || currentIndex >= originalCards.length + 2) {
      const realIndex = getRealIndex(currentIndex);
      currentIndex = realIndex + 2;
      didSnap = true;
    }

    if (!didSnap) {
      updateCardStates();
      return;
    }

    disableCardScaleTransitions();
    track.style.transition = 'none';
    setTrackPosition(currentIndex, false);
    void track.offsetWidth;
    updateCardStates();

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        window.setTimeout(() => {
          enableCardScaleTransitions();
          track.style.transition = `transform ${TRANSITION_MS}ms cubic-bezier(0.22, 0.61, 0.36, 1)`;
        }, 16);
      });
    });
  }

  function finishAnimation() {
    if (animationTimeout !== null) {
      clearTimeout(animationTimeout);
      animationTimeout = null;
    }

    isAnimating = false;
    snapIfNeeded();

    if (manualInteractionTimer === null) {
      startAutoplay();
    }
  }

  function goToTrackIndex(index) {
    if (isAnimating) return;

    isAnimating = true;
    currentIndex = index;
    updateCardStates();
    setTrackPosition(currentIndex, true);

    animationTimeout = window.setTimeout(() => {
      if (isAnimating) {
        finishAnimation();
      }
    }, TRANSITION_MS + 100);
  }

  function goToRealIndex(realIndex) {
    if (realIndex < 0 || realIndex >= originalCards.length) return;

    stopAutoplay();
    resetManualInteractionTimer();
    goToTrackIndex(realIndex + 2);
  }

  function goToAutoIndex(trackIndex) {
    if (isAnimating) return;

    isAnimating = true;
    currentIndex = trackIndex;
    updateCardStates();
    setTrackPosition(currentIndex, true);

    animationTimeout = window.setTimeout(() => {
      if (isAnimating) {
        finishAnimation();
      }
    }, TRANSITION_MS + 100);
  }

  function handleSwipe() {
    if (!isSwiping || isAnimating) return;

    const swipeDistance = touchStartX - touchEndX;
    const minSwipeDistance = 50;

    if (Math.abs(swipeDistance) < minSwipeDistance) return;

    stopAutoplay();
    resetManualInteractionTimer();

    const currentRealIndex = getRealIndex(currentIndex);

    if (swipeDistance > 0) {
      const nextRealIndex = (currentRealIndex + 1) % originalCards.length;
      goToRealIndex(nextRealIndex);
    } else {
      const prevRealIndex = (currentRealIndex - 1 + originalCards.length) % originalCards.length;
      goToRealIndex(prevRealIndex);
    }

    isSwiping = false;
  }

  track.addEventListener('transitionend', (event) => {
    if (event.propertyName !== 'transform') return;

    if (animationTimeout !== null) {
      clearTimeout(animationTimeout);
      animationTimeout = null;
    }

    finishAnimation();
  });

  track.addEventListener('touchstart', (event) => {
    isSwiping = true;
    touchStartX = event.changedTouches[0].screenX;
  });

  track.addEventListener('touchend', (event) => {
    touchEndX = event.changedTouches[0].screenX;
    handleSwipe();
  });

  indicators.forEach((button, index) => {
    button.addEventListener('click', () => {
      const currentRealIndex = getRealIndex(currentIndex);

      stopAutoplay();
      resetManualInteractionTimer();

      if (index === indicators.length - 1) {
        const nextRealIndex = (currentRealIndex + 1) % originalCards.length;
        goToRealIndex(nextRealIndex);
        return;
      }

      if (index === 0) {
        const prevRealIndex = (currentRealIndex - 1 + originalCards.length) % originalCards.length;
        goToRealIndex(prevRealIndex);
        return;
      }

      const realIndex = Number(button.dataset.indicatorIndex);
      if (!Number.isNaN(realIndex)) {
        goToRealIndex(realIndex);
      }
    });
  });

  disableCardScaleTransitions();
  setTrackPosition(currentIndex, false);
  updateCardStates();

  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      enableCardScaleTransitions();
      startAutoplay();
    });
  });

  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      stopAutoplay();
    } else {
      startAutoplay();
    }
  });

  window.addEventListener('resize', () => {
    disableCardScaleTransitions();
    setTrackPosition(currentIndex, false);
    updateCardStates();

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        enableCardScaleTransitions();
      });
    });
  });
}

export function initCarousels(root = document) {
  const carousels = root.querySelectorAll('.carousel');
  carousels.forEach((carousel) => {
    initCarousel(carousel);
  });
}

function autoInitCarousels() {
  if (window.__faCarouselInit) {
    return;
  }

  window.__faCarouselInit = true;
  initCarousels();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', autoInitCarousels);
} else {
  autoInitCarousels();
}
